import React from 'react';
import { motion, Variants } from 'framer-motion';
import { Skeleton } from '@/components/ui/skeleton';

interface ListSkeletonProps {
	/** Number of skeleton items to display */
	count?: number;
	/** Custom className for the container */
	className?: string;
	/** Variant style for the skeleton items */
	variant?: 'card' | 'compact' | 'detailed';
}

/**
 * Animated list skeleton component with staggered fade-in animation.
 * Perfect for loading states in detail views with card-based lists.
 */
export const ListSkeleton: React.FC<ListSkeletonProps> = ({
	count = 3,
	className = '',
	variant = 'card'
}) => {
	const container: Variants = {
		hidden: { opacity: 0 },
		show: {
			opacity: 1,
			transition: {
				staggerChildren: 0.1,
				delayChildren: 0.05
			}
		}
	};

	const item: Variants = {
		hidden: { opacity: 0, y: 10 },
		show: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.3,
				ease: [0.4, 0, 0.2, 1] // easeOut cubic bezier
			}
		}
	};

	return (
		<motion.div
			className={`space-y-2 ${className}`}
			variants={container}
			initial="hidden"
			animate="show"
		>
			{Array.from({ length: count }, (_, i) => (
				<motion.div key={i} variants={item}>
					{variant === 'card' && <CardSkeletonItem />}
					{variant === 'compact' && <CompactSkeletonItem />}
					{variant === 'detailed' && <DetailedSkeletonItem />}
				</motion.div>
			))}
		</motion.div>
	);
};

/**
 * Card variant - matches the contact methods, addresses, and bank details style
 */
const CardSkeletonItem = () => (
	<div className="flex flex-col gap-2 rounded-md border p-3">
		<div className="flex items-center justify-between gap-2">
			<Skeleton className="h-4 w-28" />
			<Skeleton className="h-5 w-16 rounded-full" />
		</div>
		<Skeleton className="h-4 w-full" />
		<Skeleton className="h-3 w-3/4" />
	</div>
);

/**
 * Compact variant - simpler items with less detail
 */
const CompactSkeletonItem = () => (
	<div className="flex items-center justify-between gap-4 rounded-md border p-3">
		<Skeleton className="h-4 w-32" />
		<Skeleton className="h-4 w-48" />
	</div>
);

/**
 * Detailed variant - more complex items with multiple fields
 */
const DetailedSkeletonItem = () => (
	<div className="flex flex-col gap-2 rounded-md border p-3">
		<div className="flex items-center justify-between gap-2">
			<Skeleton className="h-5 w-36" />
			<Skeleton className="h-5 w-20 rounded-full" />
		</div>
		<div className="space-y-1.5">
			<Skeleton className="h-3 w-full" />
			<Skeleton className="h-3 w-5/6" />
			<Skeleton className="h-3 w-4/6" />
		</div>
	</div>
);

/**
 * Standalone hook for creating custom list skeletons with animation
 */
export const useListSkeletonAnimation = () => {
	const containerVariants: Variants = {
		hidden: { opacity: 0 },
		show: {
			opacity: 1,
			transition: {
				staggerChildren: 0.1,
				delayChildren: 0.05
			}
		}
	};

	const itemVariants: Variants = {
		hidden: { opacity: 0, y: 10 },
		show: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.3,
				ease: [0.4, 0, 0.2, 1] // easeOut cubic bezier
			}
		}
	};

	return { containerVariants, itemVariants };
};
